import { type RewardDetail, RewardState } from "Infrastructure/Api/Api";
import {
  type RewardFormData,
  type RewardFormInput,
  rewardSchema,
} from "Infrastructure/Components/Forms/Schemas/rewardSchema";
import { formatDateOnly } from "Infrastructure/Helpers/dateFormatters";
import { useCreateOrUpdateRewardMutation } from "Rewards/Queries/useCreateOrUpdateRewardMutation";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDays, max, parseISO, startOfDay } from "date-fns";
import { useCallback, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

export const useCreateEditRewardForm = ({
  mode = "create",
  reward,
  open,
  onSuccess,
}: {
  mode?: "create" | "edit";
  reward?: RewardDetail | null;
  open: boolean;
  onSuccess?: () => void;
}) => {
  const { mutate: createOrUpdateReward, isPending } =
    useCreateOrUpdateRewardMutation();

  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);

  const parseDateOnly = useCallback(
    (dateString: string): Date => startOfDay(parseISO(dateString)),
    [],
  );

  const getDefaultValues = useCallback((): RewardFormInput => {
    if (mode === "edit" && reward) {
      return {
        name: reward.name,
        cost: reward.cost,
        startDate: reward.dateStart
          ? parseDateOnly(reward.dateStart)
          : undefined,
        endDate: reward.dateExpiration
          ? parseDateOnly(reward.dateExpiration)
          : undefined,
        description: reward.description || undefined,
        quantity: reward.totalQuantity || null,
        state: reward.state ?? RewardState.Active,
      };
    }
    return {
      name: "",
      cost: 0,
      quantity: null,
      description: undefined,
      startDate: undefined,
      endDate: undefined,
      state: RewardState.Active,
    };
  }, [mode, reward, parseDateOnly]);

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    reset,
  } = useForm<RewardFormInput>({
    resolver: zodResolver(rewardSchema),
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultValues());
    }
  }, [open, getDefaultValues, reset]);

  const watchedStartDate = useWatch({ control, name: "startDate" });

  const isStartDateInPast = Boolean(
    mode === "edit" && reward?.dateStart && new Date(reward.dateStart) < today,
  );

  const minExpirationDate = watchedStartDate
    ? max([tomorrow, addDays(watchedStartDate, 1)])
    : tomorrow;

  const handleFormSubmit = rhfHandleSubmit((data) => {
    const formData = data as RewardFormData;

    const commandData = {
      rewardID: mode === "edit" && reward ? reward.rewardID : null,
      name: formData.name,
      cost: formData.cost,
      dateStart: formData.startDate
        ? formatDateOnly(formData.startDate)
        : formatDateOnly(new Date()),
      dateExpiration: formData.endDate
        ? formatDateOnly(formData.endDate)
        : undefined,
      description: formData.description ?? undefined,
      totalQuantity: formData.quantity ?? null,
      state: formData.state,
      claimedQuantity: reward?.claimedQuantity ?? 0,
    };

    createOrUpdateReward(commandData, {
      onSuccess: () => {
        reset();
        onSuccess?.();
      },
    });
  });

  return {
    control,
    isPending,
    minExpirationDate,
    isStartDateInPast,
    handleFormSubmit,
    today,
    reset,
  } as const;
};
